import {
  GridHelper,
  Mesh,
  MeshBasicMaterial,
  Plane,
  Raycaster,
  SphereGeometry,
  Vector2,
  Vector3,
} from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { TransformControls } from 'three/addons/controls/TransformControls.js';
import { LevelCollider } from '../collision/LevelCollider';
import { Engine } from '../core/Engine';
import { Input } from '../core/Input';
import { MouseLook } from '../core/MouseLook';
import { EYE_HEIGHT } from '../movement/constants';
import { PlayerController } from '../movement/PlayerController';
import { addEnvironment } from '../world/environment';
import { loadMaterials, type MaterialSet } from '../world/materials';
import {
  emptySpec,
  meshForPlacement,
  type Placement,
  type PlacementMapSpec,
  parseSpec,
  randomSpec,
  serializeSpec,
  syncPlacement,
} from './placement';
import { SHAPE_IDS, SHAPES, type ShapeId } from './shapes';

const DRAFT_KEY = 'fall-line.editor.draft';
const TRANSLATE_SNAP = 64;
const ROTATE_SNAP = (15 * Math.PI) / 180;
type GizmoMode = 'translate' | 'rotate' | 'scale';

/**
 * In-browser spatial map editor. Place prebuilt ramp shapes, move/rotate/raise
 * them with gizmos, then jump into Play mode to surf the map in the real
 * movement engine — all in one scene. Maps save as placement JSON (the editable
 * source of truth); see docs/features for the design + phased plan.
 */
export class EditorApp {
  private readonly engine: Engine;
  private readonly orbit: OrbitControls;
  private readonly gizmo: TransformControls;
  private readonly raycaster = new Raycaster();
  private readonly groundPlane = new Plane(new Vector3(0, 1, 0), 0);
  private readonly grid: GridHelper;
  private readonly spawnMarker: Mesh;

  private materials!: MaterialSet;
  private spec: PlacementMapSpec;
  private readonly meshes: Mesh[] = [];
  private selected: Mesh | null = null;
  private pendingShape: ShapeId | null = null;
  private snap = true;

  private mode: 'edit' | 'play' = 'edit';
  private readonly input: Input;
  private readonly look: MouseLook;
  private collider: LevelCollider | null = null;
  private player: PlayerController | null = null;
  private readonly eye = new Vector3();

  // Distinguish a click (select/place) from an orbit drag.
  private downAt = new Vector2();
  private downButton = -1;

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.engine = new Engine(canvas);
    this.engine.setQuality('low');
    this.input = new Input(canvas);
    this.look = new MouseLook(this.input);

    this.engine.camera.position.set(2600, 2200, 2600);
    this.orbit = new OrbitControls(this.engine.camera, canvas);
    this.orbit.target.set(0, 400, 0);
    this.orbit.maxPolarAngle = Math.PI * 0.495; // don't drop under the floor
    this.orbit.update();

    this.grid = new GridHelper(32768, 64, 0x335577, 0x223344);
    this.engine.scene.add(this.grid);

    this.gizmo = new TransformControls(this.engine.camera, canvas);
    this.gizmo.setTranslationSnap(TRANSLATE_SNAP);
    this.gizmo.setRotationSnap(ROTATE_SNAP);
    // r169+: TransformControls is no longer an Object3D — add its helper.
    this.engine.scene.add(this.gizmo.getHelper());
    // Gizmo and orbit both want the mouse; hand off while dragging a handle.
    this.gizmo.addEventListener('dragging-changed', (e) => {
      this.orbit.enabled = !(e as unknown as { value: boolean }).value;
    });
    this.gizmo.addEventListener('objectChange', () => {
      if (this.selected) {
        syncPlacement(this.selected.userData.placement as Placement, this.selected);
        this.autosave();
        this.refreshStatus();
      }
    });

    this.spawnMarker = new Mesh(
      new SphereGeometry(48, 16, 12),
      new MeshBasicMaterial({ color: 0x4caf50, wireframe: true }),
    );
    this.engine.scene.add(this.spawnMarker);

    this.spec = this.loadDraft() ?? emptySpec();

    this.engine.onUpdate((dt) => this.update(dt));
    this.engine.onRender((alpha) => this.render(alpha));

    this.bindCanvas();
    this.bindKeys();
  }

  async init(): Promise<void> {
    await addEnvironment(this.engine.scene);
    this.materials = await loadMaterials();
    this.rebuildFromSpec();
    this.buildPalette();
    this.bindToolbar();
    this.refreshStatus();
    this.engine.start();
  }

  // ── Scene <-> spec ──────────────────────────────────────────────────────────

  private rebuildFromSpec(): void {
    this.select(null);
    for (const mesh of this.meshes) {
      this.engine.scene.remove(mesh);
    }
    this.meshes.length = 0;
    for (const p of this.spec.placements) {
      const mesh = meshForPlacement(p, this.materials);
      this.engine.scene.add(mesh);
      this.meshes.push(mesh);
    }
    this.spawnMarker.position.fromArray(this.spec.spawn);
  }

  private addShape(shapeId: ShapeId, position: Vector3): void {
    const placement: Placement = {
      shapeId,
      position: [position.x, position.y, position.z],
      rotation: [0, 0, 0],
    };
    this.spec.placements.push(placement);
    const mesh = meshForPlacement(placement, this.materials);
    this.engine.scene.add(mesh);
    this.meshes.push(mesh);
    this.select(mesh);
    this.autosave();
    this.refreshStatus();
  }

  private deleteSelected(): void {
    if (!this.selected) {
      return;
    }
    const mesh = this.selected;
    const placement = mesh.userData.placement as Placement;
    this.select(null);
    this.engine.scene.remove(mesh);
    this.meshes.splice(this.meshes.indexOf(mesh), 1);
    this.spec.placements.splice(this.spec.placements.indexOf(placement), 1);
    this.autosave();
    this.refreshStatus();
  }

  private duplicateSelected(): void {
    if (!this.selected) {
      return;
    }
    const src = this.selected.userData.placement as Placement;
    const copy: Placement = {
      shapeId: src.shapeId,
      params: src.params ? { ...src.params } : undefined,
      // Offset a grid step so the copy doesn't hide exactly under the original.
      position: [
        src.position[0] + TRANSLATE_SNAP,
        src.position[1],
        src.position[2] + TRANSLATE_SNAP,
      ],
      rotation: [...src.rotation],
      scale: src.scale ? [...src.scale] : undefined,
      role: src.role,
    };
    this.spec.placements.push(copy);
    const mesh = meshForPlacement(copy, this.materials);
    this.engine.scene.add(mesh);
    this.meshes.push(mesh);
    this.select(mesh);
    this.autosave();
    this.refreshStatus();
  }

  private select(mesh: Mesh | null): void {
    this.selected = mesh;
    if (mesh) {
      this.gizmo.attach(mesh);
    } else {
      this.gizmo.detach();
    }
    this.refreshStatus();
  }

  // ── Pointer: place / select ────────────────────────────────────────────────

  private bindCanvas(): void {
    this.canvas.addEventListener('pointerdown', (e) => {
      this.downAt.set(e.clientX, e.clientY);
      this.downButton = e.button;
    });
    this.canvas.addEventListener('pointerup', (e) => {
      if (this.mode !== 'edit' || e.button !== 0 || this.downButton !== 0) {
        return;
      }
      // Ignore orbit drags — only a near-stationary release counts as a click.
      if (this.downAt.distanceTo(new Vector2(e.clientX, e.clientY)) > 5) {
        return;
      }
      this.handleClick(e);
    });
  }

  private pointerNdc(e: PointerEvent): Vector2 {
    const rect = this.canvas.getBoundingClientRect();
    return new Vector2(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1,
    );
  }

  private handleClick(e: PointerEvent): void {
    this.raycaster.setFromCamera(this.pointerNdc(e), this.engine.camera);
    if (this.pendingShape) {
      const hit = this.raycaster.ray.intersectPlane(this.groundPlane, new Vector3());
      if (hit) {
        if (this.snap) {
          hit.x = Math.round(hit.x / TRANSLATE_SNAP) * TRANSLATE_SNAP;
          hit.z = Math.round(hit.z / TRANSLATE_SNAP) * TRANSLATE_SNAP;
        }
        this.addShape(this.pendingShape, hit);
      }
      this.setPending(null);
      return;
    }
    const [first] = this.raycaster.intersectObjects(this.meshes, false);
    this.select(first ? (first.object as Mesh) : null);
  }

  // ── Keyboard ────────────────────────────────────────────────────────────────

  private bindKeys(): void {
    window.addEventListener('keydown', (e) => {
      if (this.mode === 'play') {
        if (e.code === 'Escape') {
          this.exitPlay();
        }
        return;
      }
      switch (e.code) {
        case 'Digit1':
        case 'KeyG':
          this.setMode('translate');
          break;
        case 'Digit2':
        case 'KeyR':
          this.setMode('rotate');
          break;
        case 'Digit3':
          this.setMode('scale');
          break;
        case 'KeyD':
          this.duplicateSelected();
          break;
        case 'Delete':
        case 'Backspace':
          this.deleteSelected();
          break;
        case 'Escape':
          this.pendingShape ? this.setPending(null) : this.select(null);
          break;
      }
    });
  }

  private setMode(mode: GizmoMode): void {
    this.gizmo.setMode(mode);
    this.refreshStatus();
  }

  // ── Play mode ────────────────────────────────────────────────────────────────

  private enterPlay(): void {
    if (this.mode === 'play') {
      return;
    }
    if (this.meshes.length === 0) {
      return;
    }
    this.select(null);
    this.engine.scene.updateMatrixWorld(true);
    const level = {
      colliders: this.meshes.filter((m) => (m.userData.placement as Placement).role !== 'detail'),
      spawn: new Vector3().fromArray(this.spec.spawn),
    };
    this.collider = new LevelCollider(level.colliders);
    this.player = new PlayerController(this.collider, level.spawn, this.spec.killY ?? -2000);
    this.look.yaw = this.spec.spawnYaw;
    this.look.pitch = -0.1;
    this.mode = 'play';
    this.grid.visible = false;
    this.gizmo.getHelper().visible = false;
    this.spawnMarker.visible = false;
    this.orbit.enabled = false;
    document.body.classList.add('playing');
    this.input.requestLock();
    this.refreshStatus();
  }

  private exitPlay(): void {
    if (this.mode !== 'play') {
      return;
    }
    this.mode = 'edit';
    this.player = null;
    this.collider = null;
    this.grid.visible = true;
    this.gizmo.getHelper().visible = true;
    this.spawnMarker.visible = true;
    this.orbit.enabled = true;
    document.body.classList.remove('playing');
    if (document.pointerLockElement) {
      document.exitPointerLock();
    }
    this.refreshStatus();
  }

  private update(dt: number): void {
    if (this.mode !== 'play' || !this.player) {
      return;
    }
    this.look.update();
    this.player.tick(
      {
        forward: this.input.axis('KeyW', 'KeyS'),
        right: this.input.axis('KeyD', 'KeyA'),
        jump: this.input.pressed('Space'),
      },
      this.look.yaw,
      dt,
    );
    // Fell in the lava: PlayerController.respawn already snapped us to spawn.
  }

  private render(alpha: number): void {
    if (this.mode === 'edit') {
      this.orbit.update();
      return;
    }
    if (!this.player) {
      return;
    }
    this.eye.lerpVectors(this.player.prevPosition, this.player.position, alpha);
    this.eye.y += EYE_HEIGHT;
    this.engine.camera.position.copy(this.eye);
    this.engine.camera.rotation.set(this.look.pitch, this.look.yaw, 0, 'YXZ');
  }

  // ── Save / load ──────────────────────────────────────────────────────────────

  private autosave(): void {
    try {
      localStorage.setItem(DRAFT_KEY, serializeSpec(this.spec));
    } catch {
      // Storage unavailable — the draft just isn't persisted.
    }
  }

  private loadDraft(): PlacementMapSpec | null {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      return raw ? parseSpec(raw) : null;
    } catch {
      return null;
    }
  }

  private async save(): Promise<void> {
    const json = serializeSpec(this.spec);
    const filename = `${this.spec.id || 'map'}.json`;
    // Self-contained typing so we don't depend on the FileSystem Access lib types.
    type Writable = { write(data: string): Promise<void>; close(): Promise<void> };
    type Handle = { createWritable(): Promise<Writable> };
    const picker = (
      window as unknown as {
        showSaveFilePicker?: (o: unknown) => Promise<Handle>;
      }
    ).showSaveFilePicker;
    if (picker) {
      try {
        const handle = await picker({
          suggestedName: filename,
          types: [{ description: 'Fall Line map', accept: { 'application/json': ['.json'] } }],
        });
        const writable = await handle.createWritable();
        await writable.write(json);
        await writable.close();
        return;
      } catch {
        return; // user cancelled the picker
      }
    }
    // Fallback that works everywhere: an anchor download.
    const url = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  private loadFromText(text: string): void {
    try {
      this.spec = parseSpec(text);
    } catch (err) {
      this.toast(`Load failed: ${(err as Error).message}`);
      return;
    }
    this.rebuildFromSpec();
    this.autosave();
    this.refreshStatus();
  }

  private newMap(): void {
    this.spec = emptySpec();
    this.rebuildFromSpec();
    this.autosave();
    this.refreshStatus();
  }

  private generate(count: number): void {
    if (
      this.spec.placements.length > 0 &&
      !confirm('Replace the current map with a new random one?')
    ) {
      return;
    }
    this.spec = randomSpec(count);
    this.rebuildFromSpec();
    this.autosave();
    this.refreshStatus();
  }

  private setSpawnHere(): void {
    const from = this.selected ? this.selected.position : this.orbit.target;
    this.spec.spawn = [from.x, from.y + 120, from.z];
    this.spawnMarker.position.fromArray(this.spec.spawn);
    this.autosave();
    this.refreshStatus();
  }

  // ── UI ───────────────────────────────────────────────────────────────────────

  private setPending(shapeId: ShapeId | null): void {
    this.pendingShape = shapeId;
    for (const button of document.querySelectorAll<HTMLElement>('#palette button')) {
      button.classList.toggle('pending', button.dataset.shape === shapeId);
    }
    document.body.classList.toggle('placing', shapeId !== null);
    this.refreshStatus();
  }

  private buildPalette(): void {
    const palette = document.querySelector<HTMLElement>('#palette');
    if (!palette) {
      return;
    }
    for (const id of SHAPE_IDS) {
      const button = document.createElement('button');
      button.textContent = SHAPES[id].label;
      button.dataset.shape = id;
      button.addEventListener('click', () => this.setPending(this.pendingShape === id ? null : id));
      palette.appendChild(button);
    }
  }

  private bindToolbar(): void {
    const on = (id: string, fn: () => void) =>
      document.querySelector(`#${id}`)?.addEventListener('click', fn);
    on('mode-move', () => this.setMode('translate'));
    on('mode-rotate', () => this.setMode('rotate'));
    on('mode-scale', () => this.setMode('scale'));
    on('act-duplicate', () => this.duplicateSelected());
    on('act-delete', () => this.deleteSelected());
    on('act-spawn', () => this.setSpawnHere());
    on('act-new', () => this.newMap());
    on('act-generate', () => {
      const input = document.querySelector<HTMLInputElement>('#gen-count');
      this.generate(input ? Number(input.value) : 8);
    });
    on('act-save', () => void this.save());
    on('act-play', () => this.enterPlay());
    on('act-stop', () => this.exitPlay());

    const snapToggle = document.querySelector<HTMLInputElement>('#snap-toggle');
    if (snapToggle) {
      snapToggle.checked = this.snap;
      snapToggle.addEventListener('change', () => {
        this.snap = snapToggle.checked;
        this.gizmo.setTranslationSnap(this.snap ? TRANSLATE_SNAP : null);
        this.gizmo.setRotationSnap(this.snap ? ROTATE_SNAP : null);
      });
    }

    const fileInput = document.querySelector<HTMLInputElement>('#act-load');
    fileInput?.addEventListener('change', async () => {
      const file = fileInput.files?.[0];
      if (file) {
        this.loadFromText(await file.text());
      }
      fileInput.value = '';
    });
  }

  private refreshStatus(): void {
    const status = document.querySelector<HTMLElement>('#status');
    if (!status) {
      return;
    }
    if (this.mode === 'play') {
      status.textContent = 'PLAY — WASD + mouse to surf · Space jump · Esc to edit';
      return;
    }
    const sel = this.selected
      ? `selected ${(this.selected.userData.placement as Placement).shapeId}`
      : this.pendingShape
        ? `click the grid to place ${SHAPES[this.pendingShape].label}`
        : 'click a shape to place, or click one to select';
    status.textContent = `${this.spec.placements.length} shapes · ${this.gizmo.getMode()} · ${sel}`;
  }

  private toast(message: string): void {
    const status = document.querySelector<HTMLElement>('#status');
    if (status) {
      status.textContent = message;
    }
  }
}
